// src/pages/DescriptionPage.jsx
import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaMobileAlt, FaDollarSign, FaGlobe } from "react-icons/fa";
import { motion, useMotionValue, useTransform } from "framer-motion";
import Draggable from "react-draggable";

const DescriptionPage = () => {
  const navigate = useNavigate();
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    setMouse({ x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Floating glow effect for cards
  const floatVariants = {
    initial: { y: 0 },
    animate: { y: [0, -10, 0], transition: { duration: 3, repeat: Infinity } },
  };

  // Mouse-based tilt
  const calculateTilt = (index) => {
    const xOffset = (mouse.x - window.innerWidth / 2) / 40;
    const yOffset = (mouse.y - window.innerHeight / 2) / 40;
    return { rotateX: -yOffset, rotateY: xOffset, rotateZ: xOffset / 2 };
  };

  const features = [
    {
      title: "Real-life Alerts",
      desc: "Send real-time alerts directly to someone’s phone for instant notifications.",
      icon: FaMobileAlt,
      color: "from-purple-500 to-blue-500",
    },
    {
      title: "Bank Account Visibility",
      desc: "Instantly see payments reflected in the recipient’s bank account for full transparency.",
      icon: FaDollarSign,
      color: "from-green-400 to-teal-500",
    },
    {
      title: "International Transfers",
      desc: "Send money to any foreign account worldwide safely and efficiently.",
      icon: FaGlobe,
      color: "from-yellow-400 to-orange-500",
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Background animated neon grids */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div
          className="absolute w-[200%] h-[200%] bg-gradient-to-r from-purple-700 via-blue-700 to-pink-700 opacity-10 blur-3xl animate-[pulse_10s_infinite]"
          style={{ top: "-50%", left: "-50%" }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 flex flex-col items-center">
        <motion.h1
          className="text-5xl md:text-6xl font-extrabold text-center mb-8 text-cyan-400 tracking-wide neon-text"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1 }}
        >
          Welcome to Probank 15v
        </motion.h1>

        <motion.p
          className="text-center text-gray-300 mb-12 text-lg md:text-xl max-w-3xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
        >
          Discover everything you can do on Probank 15v! Our platform is designed to make managing and tracking payments and alerts fast, easy, and secure.
        </motion.p>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const tilt = calculateTilt(index);
            return (
              <motion.div
                key={index}
                className={`relative bg-gradient-to-br ${feature.color} p-6 rounded-3xl shadow-2xl cursor-pointer border border-white/10`}
                style={{
                  transformStyle: "preserve-3d",
                  perspective: 1000,
                  ...tilt,
                }}
                variants={floatVariants}
                initial="initial"
                animate="animate"
                whileHover={{ scale: 1.05, rotateX: tilt.rotateX * 1.5, rotateY: tilt.rotateY * 1.5 }}
              >
                <div className="flex items-start space-x-4">
                  <Icon className="text-white text-4xl glow-icon" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-gray-200 text-sm">{feature.desc}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          className="mt-12 text-gray-300 text-center text-lg md:text-xl max-w-3xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 1 }}
        >
          Whether you’re handling everyday payments, urgent alerts, or international transfers, Probank 15v ensures everything is smooth, secure, and reliable.
        </motion.p>

        {/* Back to Dashboard */}
        <motion.button
          onClick={() => navigate("/dashboard")}
          className="mt-12 px-8 py-4 rounded-full bg-cyan-500 hover:bg-cyan-600 font-bold text-lg shadow-lg neon-glow transition-transform hover:scale-105"
          whileHover={{ scale: 1.1 }}
        >
          Back to Dashboard
        </motion.button>
      </div>

      {/* Draggable Subscribe Button */}
      <Draggable>
        <motion.button
          onClick={() => navigate("/subscription")}
          className="fixed bottom-10 right-10 px-6 py-4 rounded-full bg-green-400 text-black font-bold shadow-xl neon-glow cursor-move z-50"
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          Subscribe
        </motion.button>
      </Draggable>

      {/* Custom styles */}
      <style>
        {`
          .glow-icon {
            text-shadow: 0 0 10px rgba(0,255,255,0.6), 0 0 20px rgba(0,255,255,0.4);
          }
          .neon-glow {
            box-shadow: 0 0 15px rgba(0,255,255,0.6), 0 0 30px rgba(0,255,255,0.4);
          }
          .neon-text {
            text-shadow: 0 0 5px #00f0ff, 0 0 10px #00f0ff, 0 0 20px #00f0ff;
          }
          @keyframes pulse {
            0%, 100% { transform: translate(0,0) scale(1); }
            50% { transform: translate(5px, -5px) scale(1.02); }
          }
        `}
      </style>
    </div>
  );
};

export default DescriptionPage;
